import React, { useState } from 'react';
import { Check, CheckCircle2, ChevronDown, ChevronRight, Lightbulb, Loader2 } from 'lucide-react';
import type { SigeHinweis } from './planungAnbindung';

// Hinweise aus der Planungsphase: beantwortete Fragen mit Zielobjekt
// SiGe-Plan, einklappbar. „Berücksichtigt" stempelt `uebernommen_am` an der
// Frage — per Klick, nie von selbst (Entscheid E7). Die Liste zeigt nur an
// und ruft auf; was gestempelt wird, entscheidet der Assistent.

interface Props {
  hinweise: readonly SigeHinweis[];
  onBeruecksichtigt: (hinweis: SigeHinweis) => Promise<void>;
  /** Aufgeklappt öffnen (Schritt 2); in der Prüfung bleibt die Liste zu. */
  standardOffen?: boolean;
}

const KRITIK_STIL: Record<SigeHinweis['kritikalitaet'], string> = {
  A: 'bg-red-50 text-red-700 border-red-200',
  B: 'bg-amber-50 text-amber-700 border-amber-200',
  C: 'bg-slate-50 text-slate-600 border-slate-200',
};

/** ISO-Zeitstempel → TT.MM.JJJJ durch Zerlegen (kein Date-Parsing, keine Zeitzonenverschiebung). */
const tagAus = (iso: string | null): string => {
  const t = String(iso || '').slice(0, 10);
  const m = t.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : '';
};

export const PlanungHinweise: React.FC<Props> = ({ hinweise, onBeruecksichtigt, standardOffen = false }) => {
  const [offen, setOffen] = useState(standardOffen);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  if (hinweise.length === 0) return null;
  const offenAnzahl = hinweise.filter(h => !h.uebernommenAm).length;

  const stempeln = async (h: SigeHinweis) => {
    setLaeuft(h.code);
    try {
      await onBeruecksichtigt(h);
    } finally {
      setLaeuft(null);
    }
  };

  return (
    <section className="bg-blue-50 border border-blue-200 rounded-xl">
      <button
        type="button"
        onClick={() => setOffen(o => !o)}
        className="w-full flex items-center gap-2 px-4 py-2.5 text-left"
        aria-expanded={offen}
      >
        {offen ? <ChevronDown className="w-4 h-4 text-blue-500 shrink-0" /> : <ChevronRight className="w-4 h-4 text-blue-500 shrink-0" />}
        <Lightbulb className="w-4 h-4 text-blue-600 shrink-0" />
        <span className="text-sm font-bold text-blue-900 flex-1 min-w-0 truncate">
          Hinweise aus der Planungsphase ({hinweise.length})
        </span>
        <span className="text-[11px] text-blue-800 shrink-0">
          {offenAnzahl === 0 ? 'alle berücksichtigt' : `${offenAnzahl} noch nicht berücksichtigt`}
        </span>
      </button>

      {offen && (
        <div className="border-t border-blue-200">
          <p className="px-4 pt-2.5 text-[11px] text-blue-800 leading-snug">
            Beantwortete Fragen der Planungsphase mit Bezug zum SiGe-Plan. Sie ändern nichts am Plan —
            was daraus in Arbeitsschritte oder Maßnahmen gehört, entscheidet der Koordinator.
            „Berücksichtigt“ stempelt die Frage in der Planungsphase als übernommen.
          </p>
          <ul className="divide-y divide-blue-100">
            {hinweise.map(h => (
              <li key={h.code} className="px-4 py-2.5 flex items-start gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-mono text-slate-500">{h.code}</span>
                    <span className={`text-[10px] font-black px-1.5 py-0.5 rounded border ${KRITIK_STIL[h.kritikalitaet]}`}>
                      {h.kritikalitaet}
                    </span>
                    {h.status === 'teilbeantwortet' && (
                      <span className="text-[10px] font-bold px-1.5 py-0.5 rounded border bg-amber-50 text-amber-700 border-amber-200">
                        teilbeantwortet
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-slate-700 leading-snug mt-0.5">{h.frage}</div>
                  <div className="text-xs text-slate-900 leading-snug mt-1 whitespace-pre-wrap">
                    <span className="font-bold">Antwort:</span> {h.antwort}
                  </div>
                </div>
                <div className="shrink-0">
                  {h.uebernommenAm ? (
                    <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700" title={`Berücksichtigt am ${tagAus(h.uebernommenAm)}`}>
                      <CheckCircle2 className="w-3.5 h-3.5" /> berücksichtigt{tagAus(h.uebernommenAm) ? ` ${tagAus(h.uebernommenAm)}` : ''}
                    </span>
                  ) : (
                    <button
                      type="button"
                      onClick={() => { void stempeln(h); }}
                      disabled={laeuft !== null || !h.frageId}
                      title={h.frageId
                        ? 'Als berücksichtigt stempeln — die Frage gilt in der Planungsphase als in den SiGe-Plan übernommen'
                        : 'Die Frage ist nicht zuzuordnen — bitte in der Planungsphase prüfen'}
                      className="inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-bold rounded-md border text-blue-700 bg-white border-blue-300 hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                    >
                      {laeuft === h.code ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Check className="w-3.5 h-3.5" />}
                      berücksichtigt
                    </button>
                  )}
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </section>
  );
};
